<script setup lang="ts">
import GSheetDialogForm from '@consumer/components/GSheetDialogForm.vue'
import LocationPicker from '@consumer/modules/shared/LocationPicker.vue'
import { locations as locationsApi, userGiftPreferences } from '@consumer/api'

const open = (defineModel<boolean>('open', { required: true }))

const emit = defineEmits<{
  saved: [value: string]
}>()

const selected = ref<string[]>([])
const popularLocations = ref<{ label: string; value: string; imageUrl?: string }[]>([])

// Load popular cities the first time the dialog opens rather than on mount —
// this dialog is rendered (closed) on every logged-in page, so an on-mount
// fetch would hit the API on every page load.
let loaded = false
watch(() => open.value, async (isOpen) => {
  if (!isOpen || loaded) return
  try {
    popularLocations.value = await locationsApi.popular({ visit: false })
    loaded = true
  }
  catch {
    // Popular cities are a non-critical convenience (the typeahead still works),
    // so leave `loaded` false to retry on the next open rather than surfacing an error.
  }
})

async function onSubmit () {
  if (!selected.value.length) return

  await userGiftPreferences.updateCategory({
    data: { category: 'Locations', values: selected.value },
  })
  emit('saved', selected.value[0])
}
</script>

<template>
  <GSheetDialogForm
    v-model:open="open"
    name="add-profile-location-dialog"
    :onSubmit="onSubmit"
    :submitDisabled="!selected.length"
    submitLabel="Save"
    pillButtons
  >
    <template #header>
      <span class="g-sheet-dialog-title">Add your location</span>
    </template>

    <div class="flex flex-col gap-6">
      <p class="text-sm text-v2-secondary text-center">
        Add the cities where you'll use your gifts so friends can send options that work locally.
      </p>
      <LocationPicker
        v-model="selected"
        :popularOptions="popularLocations"
      />
    </div>
  </GSheetDialogForm>
</template>
